import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { toast } from 'react-toastify';

function Employees() {
  const navigate = useNavigate();
  // State for employee data
  const [employees, setEmployees] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);

  // Form data state for new/edit employee
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phoneNumber: '',
    role: '', // OPERATIONS_MANAGER, SUPPORT_AGENT, CONTENT_MARKETING_MANAGER, FINANCE_COMPLIANCE_MANAGER
    access: {},
  });

  const tableRef = useRef(null);

  // Fetch employees and permissions on component mount
  useEffect(() => {
    fetchEmployees();
    fetchPermissions();
  }, []);

  async function fetchEmployees() {
    setLoading(true);
    try {
      const data = await api.getAdmins();
      setEmployees(data);
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast.error("Failed to load employees.");
    } finally {
      setLoading(false);
    }
  }

  async function fetchPermissions() {
    try {
      const data = await api.getAdminPermissions();
      setPermissions(data);
    } catch (error) {
      console.error("Error fetching permissions:", error);
      toast.error("Failed to load permissions.");
    }
  }

  function handleInputChange(event) {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }

  function handleAccessChange(event) {
    const { name, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      access: {
        ...prev.access,
        [name]: checked,
      },
    }));
  }

  function resetForm() {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      phoneNumber: '',
      role: '',
      access: {},
    });
    setEditingEmployeeId(null);
  }

  async function handleAddOrUpdateEmployee(event) {
    event.preventDefault();
    setSubmitting(true);

    try {
      let response;
      if (editingEmployeeId) {
        response = await api.updateAdmin(editingEmployeeId, formData);
      } else {
        response = await api.createAdmin(formData);
      }

      if (response.ok) {
        toast.success(response.message || `Employee ${editingEmployeeId ? 'updated' : 'created'} successfully!`);
        fetchEmployees();
        resetForm();
        // Close modal if open
        const modalEl = document.getElementById(editingEmployeeId ? 'editEmployee' : 'addEmployee');
        if (modalEl && window.bootstrap) {
          const modal = window.bootstrap.Modal.getInstance(modalEl) || new window.bootstrap.Modal(modalEl);
          modal.hide();
        }
      } else {
        const errorMessage = Array.isArray(response.message) ? response.message.join(', ') : response.message || `Failed to ${editingEmployeeId ? 'update' : 'create'} employee.`;
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error(`Error ${editingEmployeeId ? 'updating' : 'creating'} employee:`, error);
      toast.error(`An error occurred while ${editingEmployeeId ? 'updating' : 'creating'} the employee.`);
    } finally {
      setSubmitting(false);
    }
  }

  function handleEditClick(employee) {
    if (!employee || !employee.id) {
      toast.error("Cannot edit: Employee ID is missing.");
      return;
    }
    setEditingEmployeeId(employee.id);
    setFormData({
      firstName: employee.firstName,
      lastName: employee.lastName,
      email: employee.email,
      phoneNumber: employee.phoneNumber,
      role: employee.role,
      access: employee.access || {},
      password: '', // Password is not editable in this form
    });
    // Open edit modal
    try {
      const modalEl = document.getElementById('editEmployee');
      if (modalEl && window.bootstrap) {
        const modal = new window.bootstrap.Modal(modalEl);
        modal.show();
      }
    } catch (_) { /* ignore */ }
  }

  function handleCancelEdit() {
    resetForm();
    // Close edit modal
    try {
      const modalEl = document.getElementById('editEmployee');
      if (modalEl && window.bootstrap) {
        const modal = window.bootstrap.Modal.getInstance(modalEl) || new window.bootstrap.Modal(modalEl);
        modal.hide();
      }
    } catch (_) { /* ignore */ }
  }

  function handleDelete(id) {
    // Placeholder for delete functionality
    toast.info("Delete functionality not yet implemented.");
    console.log("Delete employee with ID:", id);
  }

  // Initialize DataTable and re-draw on data changes
  useEffect(() => {
    // No longer using jQuery DataTables, so this useEffect is removed.
    // The table rendering is now fully handled by React state.
  }, [employees]);

  return (
    <>
      <Helmet>
        <title>Employees | Owena Admin Dashboard</title>
      </Helmet>
      <div className="ec-content-wrapper">
        <div className="content">
          <div className="breadcrumb-wrapper breadcrumb-contacts">
            <div>
              <h1>Employees</h1>
              <p className="breadcrumbs">
                <span>
                  <a href="#" onClick={(e) => e.preventDefault()}>Home</a>
                </span>
                <span><i className="mdi mdi-chevron-right"></i></span>
                Employees
              </p>
            </div>
            <div>
              <button type="button" className="btn btn-primary" data-bs-toggle="modal" data-bs-target="#addEmployee">
                Add Employee
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="ec-vendor-list card card-default">
                <div className="card-body">
                  {loading ? (
                    <p>Loading employees...</p>
                  ) : employees.length === 0 ? (
                    <p>No employees found.</p>
                  ) : (
                    <div className="table-responsive">
                      <table id="employees-table" ref={tableRef} className="table">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th>Joined</th>
                            <th>Action</th>
                          </tr>
                        </thead>

                        <tbody>
                          {employees.map(emp => (
                            <tr key={emp.id || `employee-${emp.email}`}>
                              <td>{emp.id}</td>
                              <td>{`${emp.firstName} ${emp.lastName}`}</td>
                              <td>{emp.email}</td>
                              <td>{emp.phoneNumber}</td>
                              <td>{emp.role}</td>
                              <td>
                                <span className={`ec-status ${emp.isActive ? 'ec-active' : 'ec-inactive'}`}>{emp.isActive ? 'Active' : 'Inactive'}</span>
                              </td>
                              <td>{new Date(emp.createdAt).toLocaleDateString()}</td>
                              <td>
                                <div className="btn-group mb-1">
                                  <button
                                    type="button"
                                    className="btn btn-outline-success"
                                    onClick={() => { if (emp.id) navigate(`/employees/${emp.id}`); else toast.error("Employee ID is missing for navigation."); }}
                                  >
                                    Info
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-success dropdown-toggle dropdown-toggle-split"
                                    data-bs-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                    data-display="static"
                                  >
                                    <span className="sr-only">Info</span>
                                  </button>

                                  <div className="dropdown-menu">
                                    <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleEditClick(emp); }}>Edit</a>
                                    <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleDelete(emp.id); }}>Delete</a>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Add Employee Modal */}
          <div className="modal fade" id="addEmployee" tabIndex="-1" aria-labelledby="addEmployeeLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header px-4">
                  <h5 className="modal-title" id="addEmployeeLabel">{editingEmployeeId ? 'Edit Employee' : 'Add Employee'}</h5>
                  <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={resetForm}></button>
                </div>
                <div className="modal-body px-4">
                  <form onSubmit={handleAddOrUpdateEmployee}>
                    <div className="mb-3">
                      <label htmlFor="emp-firstName" className="form-label">First Name</label>
                      <input id="emp-firstName" name="firstName" type="text" className="form-control" value={formData.firstName} onChange={handleInputChange} required />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="emp-lastName" className="form-label">Last Name</label>
                      <input id="emp-lastName" name="lastName" type="text" className="form-control" value={formData.lastName} onChange={handleInputChange} required />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="emp-email" className="form-label">Email</label>
                      <input id="emp-email" name="email" type="email" className="form-control" value={formData.email} onChange={handleInputChange} required />
                    </div>
                    {!editingEmployeeId && (
                      <div className="mb-3">
                        <label htmlFor="emp-password" className="form-label">Password</label>
                        <input id="emp-password" name="password" type="password" className="form-control" value={formData.password} onChange={handleInputChange} required />
                      </div>
                    )}
                    <div className="mb-3">
                      <label htmlFor="emp-phoneNumber" className="form-label">Phone Number</label>
                      <input id="emp-phoneNumber" name="phoneNumber" type="text" className="form-control" value={formData.phoneNumber} onChange={handleInputChange} />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="emp-role" className="form-label">Role</label>
                      <select id="emp-role" name="role" className="form-select" value={formData.role} onChange={handleInputChange} required>
                        <option value="">Select Role</option>
                        <option value="OPERATIONS_MANAGER">Operations Manager</option>
                        <option value="SUPPORT_AGENT">Support Agent</option>
                        <option value="CONTENT_MARKETING_MANAGER">Content Marketing Manager</option>
                        <option value="FINANCE_COMPLIANCE_MANAGER">Finance Compliance Manager</option>
                      </select>
                    </div>
                    <div className="mb-4">
                      <label className="form-label">Access Permissions</label>
                      {permissions.length === 0 ? (
                        <p>No permissions available.</p>
                      ) : (
                        <div className="row">
                          {permissions.map(p => (
                            <div className="col-md-6" key={p}>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  id={`access-${p}`}
                                  name={p}
                                  checked={!!formData.access[p]}
                                  onChange={handleAccessChange}
                                />
                                <label className="form-check-label" htmlFor={`access-${p}`}>{p}</label>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="d-flex justify-content-end gap-2">
                      <button type="button" className="btn btn-secondary" data-bs-dismiss="modal" onClick={resetForm}>Cancel</button>
                      <button type="submit" className="btn btn-primary" disabled={submitting}>
                        {submitting ? (editingEmployeeId ? 'Updating...' : 'Adding...') : (editingEmployeeId ? 'Update Employee' : 'Add Employee')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* Edit Employee Modal (re-using add modal for now) */}
          <div className="modal fade" id="editEmployee" tabIndex="-1" aria-labelledby="editEmployeeLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header px-4">
                  <h5 className="modal-title" id="editEmployeeLabel">Edit Employee</h5>
                  <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={handleCancelEdit}></button>
                </div>
                <div className="modal-body px-4">
                  <form onSubmit={handleAddOrUpdateEmployee}>
                    <div className="mb-3">
                      <label htmlFor="edit-firstName" className="form-label">First Name</label>
                      <input id="edit-firstName" name="firstName" type="text" className="form-control" value={formData.firstName} onChange={handleInputChange} required />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="edit-lastName" className="form-label">Last Name</label>
                      <input id="edit-lastName" name="lastName" type="text" className="form-control" value={formData.lastName} onChange={handleInputChange} required />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="edit-email" className="form-label">Email</label>
                      <input id="edit-email" name="email" type="email" className="form-control" value={formData.email} onChange={handleInputChange} required />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="edit-phoneNumber" className="form-label">Phone Number</label>
                      <input id="edit-phoneNumber" name="phoneNumber" type="text" className="form-control" value={formData.phoneNumber} onChange={handleInputChange} />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="edit-role" className="form-label">Role</label>
                      <select id="edit-role" name="role" className="form-select" value={formData.role} onChange={handleInputChange} required>
                        <option value="">Select Role</option>
                        <option value="OPERATIONS_MANAGER">Operations Manager</option>
                        <option value="SUPPORT_AGENT">Support Agent</option>
                        <option value="CONTENT_MARKETING_MANAGER">Content Marketing Manager</option>
                        <option value="FINANCE_COMPLIANCE_MANAGER">Finance Compliance Manager</option>
                      </select>
                    </div>
                    <div className="mb-4">
                      <label className="form-label">Access Permissions</label>
                      {permissions.length === 0 ? (
                        <p>No permissions available.</p>
                      ) : (
                        <div className="row">
                          {permissions.map(p => (
                            <div className="col-md-6" key={p}>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  id={`edit-access-${p}`}
                                  name={p}
                                  checked={!!formData.access[p]}
                                  onChange={handleAccessChange}
                                />
                                <label className="form-check-label" htmlFor={`edit-access-${p}`}>{p}</label>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="d-flex justify-content-end gap-2">
                      <button type="button" className="btn btn-secondary" data-bs-dismiss="modal" onClick={handleCancelEdit}>Cancel</button>
                      <button type="submit" className="btn btn-primary" disabled={submitting}>
                        {submitting ? 'Updating...' : 'Save Changes'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Employees;
